<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Details
            </h3>

            <p>
              Edit the details of the portal.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <TextFormGroup
                    v-model="data.name"
                    name="name"
                    label="Name"
                    help-text="The name of the portal."
                    :error="getError('name')"
                />

                <TextFormGroup
                    v-model="data.display_name"
                    name="display_name"
                    label="Display Name"
                    help-text="The display name of the portal."
                    :error="getError('display_name')"
                />

                <SelectFormGroup
                    v-model="data.locale"
                    name="locale"
                    label="Locale"
                    help-text="The locale of the portal."
                    :error="getError('locale')"
                    :options="[{value: 'da_DK', label: 'Danish'}, {value: 'nb_NO', label: 'Norwegian'}, {value: 'sv_SE', label: 'Swedish'}]"
                />

                <ColorFormGroup
                    v-model="data.color"
                    name="color"
                    label="Color"
                    help-text="The color of the portal."
                    :colors="colors.data"
                    :error="getError('color')"
                />

                <TextFormGroup
                    v-model="data.base_url"
                    name="base_url"
                    label="Base URL"
                    help-text="The base URL of the portal."
                    :error="getError('base_url')"
                />

                <TextFormGroup
                    v-model="data.marketing_site_partner_url"
                    name="marketing_site_partner_url"
                    label="Marketing Site Partner URL"
                    help-text="The URL for partners on the marketing site."
                    :error="getError('marketing_site_partner_url')"
                />

                <TextFormGroup
                    v-model="data.newsletter_signup_headline"
                    name="newsletter_signup_headline"
                    label="Newsletter Signup Headline"
                    help-text="The headline of the popup and block that allow visitors to sign up for the newsletter."
                    :error="getError('newsletter_signup_headline')"
                />

                <TextareaFormGroup
                    v-model="data.newsletter_signup_text"
                    name="newsletter_signup_text"
                    label="Newsletter Signup Text"
                    help-text="The text of the popup and block that allow visitors to sign up for the newsletter."
                    :error="getError('newsletter_signup_text')"
                />

                <TextFormGroup
                    v-model="data.newsletter_signup_path"
                    name="newsletter_signup_path"
                    label="Newsletter Signup Path"
                    help-text="The path to the terms and conditions for signing up to the newsletter."
                    :error="getError('newsletter_signup_path')"
                />

                <SelectFormGroup
                    v-model="data.newsletter_signup_list_reference"
                    name="newsletter_signup_list_reference"
                    label="Newsletter Signup List"
                    help-text="The mailing list to sign visitors up for when submitting the signup form.."
                    :error="getError('newsletter_signup_list_reference')"
                    :options="mailingListOptions"
                />

                <SelectFormGroup
                    v-model="data.accounting_system_number"
                    name="accounting_system_number"
                    label="Accounting System Reference"
                    help-text="The portal in the accounting system that corresponds to this portal."
                    :error="getError('accounting_system_number')"
                    :options="accountingSystemNumberOptions"
                />

                <SelectFormGroup
                    v-model="data.company_subscription_product_number"
                    name="company_subscription_product_number"
                    label="Company Subscription Product"
                    help-text="The product in the accounting system that corresponds to company subscriptions."
                    :error="getError('company_subscription_product_number')"
                    :options="accountingSystemProductOptions"
                />

                <SelectFormGroup
                    v-model="data.person_subscription_product_number"
                    name="person_subscription_product_number"
                    label="Person Subscription Product"
                    help-text="The product in the accounting system that corresponds to person subscriptions."
                    :error="getError('person_subscription_product_number')"
                    :options="accountingSystemProductOptions"
                />

                <TextFormGroup
                    v-model="data.facebook_url"
                    name="facebook_url"
                    label="Facebook URL"
                    :error="getError('facebook_url')"
                />

                <TextFormGroup
                    v-model="data.instagram_url"
                    name="instagram_url"
                    label="Instagram URL"
                    :error="getError('instagram_url')"
                />

                <TextFormGroup
                    v-model="data.twitter_url"
                    name="twitter_url"
                    label="Twitter URL"
                    :error="getError('twitter_url')"
                />

                <TextFormGroup
                    v-model="data.linkedin_url"
                    name="linkedin_url"
                    label="LinkedIn URL"
                    :error="getError('linkedin_url')"
                />

                <TextFormGroup
                    v-model="data.design_color"
                    name="design_color"
                    label="Design Color"
                    :error="getError('design_color')"
                />

                <TextFormGroup
                    v-model="data.google_tag_manager_reference"
                    name="google_tag_manager_reference"
                    label="Google Tag Manager Reference"
                    :error="getError('google_tag_manager_reference')"
                />

                <NumberFormGroup
                    v-model="data.average_reading_time"
                    name="average_reading_time"
                    label="Average Reading Time (seconds)"
                    :error="getError('average_reading_time')"
                />

                <RadioFormGroup
                    v-model="data.design_has_rounded_corners"
                    name="design_has_rounded_corners"
                    label="Design Has Rounded Corners"
                    :error="getError('design_has_rounded_corners')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                />

                <TextareaFormGroup
                    v-model="data.partner_job_promotion_title"
                    name="partner_job_promotion_title"
                    label="Partner Job Promotion Title"
                    :error="getError('partner_job_promotion_title')"
                />

                <CkEditorFormGroup
                    v-model="data.partner_job_promotion_description"
                    name="partner_job_promotion_description"
                    label="Partner Job Promotion Description"
                    :error="getError('partner_job_promotion_description')"
                />

                <TextareaFormGroup
                    v-model="data.partner_article_promotion_title"
                    name="partner_article_promotion_title"
                    label="Partner Article Promotion Title"
                    :error="getError('partner_article_promotion_title')"
                />

                <CkEditorFormGroup
                    v-model="data.partner_article_promotion_description"
                    name="partner_article_promotion_description"
                    label="Partner Article Promotion Description"
                    :error="getError('partner_article_promotion_description')"
                />

                <TextareaFormGroup
                    v-model="data.partner_article_promotion_opt_in_text"
                    name="partner_article_promotion_opt_in_text"
                    label="Partner Article Promotion Opt In Text"
                    :error="getError('partner_article_promotion_opt_in_text')"
                />

                <TextareaFormGroup
                    v-model="data.partner_article_promotion_opt_out_text"
                    name="partner_article_promotion_opt_out_text"
                    label="Partner Article Promotion Opt Out Text"
                    :error="getError('partner_article_promotion_opt_out_text')"
                />

                <CkEditorFormGroup
                    v-model="data.partner_topic_description"
                    name="partner_topic_description"
                    label="Partner Topic Description"
                    :error="getError('partner_topic_description')"
                />

                <SelectFormGroup
                    v-model="data.large_partner_time_based_ad_format_id"
                    name="large_partner_time_based_ad_format_id"
                    label="Large Partner Time Based Ad"
                    help-text="The ad format to use for large promotion of partner articles."
                    :error="getError('large_partner_time_based_ad_format_id')"
                    :options="timeBasedAdFormatOptions"
                    :nullable="true"
                />

                <SelectFormGroup
                    v-model="data.normal_partner_time_based_ad_format_id"
                    name="normal_partner_time_based_ad_format_id"
                    label="Normal Partner Time Based Ad"
                    help-text="The ad format to use for normal promotion of partner articles."
                    :error="getError('normal_partner_time_based_ad_format_id')"
                    :options="timeBasedAdFormatOptions"
                    :nullable="true"
                />

                <SelectFormGroup
                    v-model="data.large_partner_content_article_flag_id"
                    name="large_partner_content_article_flag_id"
                    label="Large Partner Content Article Flag"
                    help-text="The content article flag to use for large promotion of partner articles."
                    :error="getError('large_partner_content_article_flag_id')"
                    :options="contentArticleFlagOptions"
                    :nullable="true"
                />

                <SelectFormGroup
                    v-model="data.normal_partner_content_article_flag_id"
                    name="normal_partner_content_article_flag_id"
                    label="Normal Partner Content Article Flag"
                    help-text="The content article flag to use for normal promotion of partner articles."
                    :error="getError('normal_partner_content_article_flag_id')"
                    :options="contentArticleFlagOptions"
                    :nullable="true"
                />

                <NumberFormGroup
                    v-model="data.latest_content_green_threshold"
                    name="latest_content_green_threshold"
                    label="Latest Content Green Threshold"
                    help-text="The number of days since the latest content published that is the limit for the green status."
                    :error="getError('latest_content_green_threshold')"
                />

                <NumberFormGroup
                    v-model="data.latest_content_amber_threshold"
                    name="latest_content_amber_threshold"
                    label="Latest Content Amber Threshold"
                    help-text="The number of days since the latest content published that is the limit for the amber status."
                    :error="getError('latest_content_amber_threshold')"
                />

                <CkEditorFormGroup
                    v-model="data.article_publication_email_template"
                    name="article_publication_email_template"
                    label="Article Publication Email Template"
                    :error="getError('article_publication_email_template')"
                />

                <GalleryFormGroup
                    v-model="data.logo_image_id"
                    name="logo_image_id"
                    label="Logo"
                    :error="getError('logo_image_id')"
                    :nullable="true"
                />

                <GalleryFormGroup
                    v-model="data.favicon_image_id"
                    name="favicon_image_id"
                    label="Favicon"
                    :error="getError('favicon_image_id')"
                    :nullable="true"
                />

                <GalleryFormGroup
                    v-model="data.newsletter_signup_image_id"
                    name="newsletter_signup_image_id"
                    label="Newsletter Signup Image"
                    :error="getError('newsletter_signup_image_id')"
                    :nullable="true"
                    help-text="The image to display in the popup and block that allow visitors to sign up for the newsletter."
                />

                <radio-form-group
                    v-model="data.partnership_activity_mail_schedule"
                    name="partnership_activity_mail_schedule"
                    label="Partnership Activity Mail Schedule"
                    :error="getError('partnership_activity_mail_schedule')"
                    :options="[{value: 'monthly', label: 'Monthly'}, {value: 'quarterly', label: 'Quarterly'}]"
                ></radio-form-group>

                <NumberFormGroup
                    v-model="data.partnership_activity_mail_page_view_increase_threshold"
                    name="partnership_activity_mail_page_view_increase_threshold"
                    label="Partnership Activity Mail Page View Increase Threshold"
                    help-text="The increase in percent that partners' content must have achieved in order for the Page View Increase block to be shown (block 2)."
                    :error="getError('partnership_activity_mail_page_view_increase_threshold')"
                />

                <NumberFormGroup
                    v-model="data.partnership_activity_mail_new_content_count_threshold"
                    name="partnership_activity_mail_new_content_count_threshold"
                    label="Partnership Activity Mail New Content Count Threshold"
                    help-text="The number of new content published that triggers the summarized new published content block (block 3)."
                    :error="getError('partnership_activity_mail_new_content_count_threshold')"
                />

                <NumberFormGroup
                    v-model="data.partnership_activity_mail_top_performing_content_threshold"
                    name="partnership_activity_mail_top_performing_content_threshold"
                    label="Partnership Activity Mail Top Performing Content Threshold"
                    help-text="The top percentage that content must be in to be included in the Top Performing Content block (block 4)."
                    :error="getError('partnership_activity_mail_top_performing_content_threshold')"
                />

                <NumberFormGroup
                    v-model="data.partnership_activity_mail_page_views_by_content_type_threshold"
                    name="partnership_activity_mail_page_views_by_content_type_threshold"
                    label="Partnership Activity Mail Page Views by Content Type Threshold"
                    help-text="The number of page views that content must have received in order for the Page Views by Content Type block to be shown (block 5)."
                    :error="getError('partnership_activity_mail_page_views_by_content_type_threshold')"
                />

                <CkEditorFormGroup
                    v-model="data.partnership_activity_mail_promotion"
                    name="partnership_activity_mail_promotion"
                    label="Partnership Activity Mail Promotion"
                    help-text="A promotion to send with the partnership activity mails (block 6)."
                    :error="getError('partnership_activity_mail_promotion')"
                />

                <hr>

                <SubmitFormGroup button-class="btn-black"/>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import CkEditorFormGroup from '@/vendor/forms/FormGroups/Horizontal/CkEditor'
import ColorFormGroup from '@/components/FormGroups/Horizontal/Color'
import EmailFormGroup from '@/vendor/forms/FormGroups/Horizontal/Email'
import Errors from '@/mixins/Errors'
import GalleryFormGroup from '@/components/FormGroups/Horizontal/Gallery'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TextareaFormGroup from '@/vendor/forms/FormGroups/Horizontal/Textarea'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Horizontal/Typeahead'

export default {
  components: {
    CkEditorFormGroup,
    ColorFormGroup,
    EmailFormGroup,
    GalleryFormGroup,
    HelpText,
    LoadingContainer,
    NumberFormGroup,
    RadioFormGroup,
    SelectFormGroup,
    SubmitFormGroup,
    TextFormGroup,
    TextareaFormGroup,
    TypeaheadFormGroup,
  },

  mixins: [Errors, Load],

  model: {
    prop: 'portal',
    event: 'portal-updated'
  },

  props: ['portal'],

  data() {
    return {
      accountingSystemPortals: [],
      accountingSystemProducts: [],
      mailingLists: [],
      colors: [],

      data: {...this.portal.attributes},

      shared: store,
    }
  },

  mounted() {
    this.load({
      accountingSystemPortals: '/api/v2/bookkeeping-departments',
      accountingSystemProducts: '/api/v2/bookkeeping-products',
      mailingLists: '/api/v2/external/mailing-lists',
      colors: '/api/v2/colors',
      timeBasedAdFormats: '/api/v2/time-based-ad-formats?filter[portal_id]=' + this.portal.id,
      contentArticleFlags: '/api/v2/content-article-flags?filter[portal_id]=' + this.portal.id,
    })
  },

  computed: {
    accountingSystemNumberOptions() {
      return this.accountingSystemPortals.data.map(accountingSystemPortal => {
        return {
          value: accountingSystemPortal.id,
          label: accountingSystemPortal.attributes.name
        }
      })
    },

    accountingSystemProductOptions() {
      return this.accountingSystemProducts.data.map(accountingSystemProduct => {
        return {
          value: accountingSystemProduct.id,
          label: accountingSystemProduct.attributes.name
        }
      })
    },

    mailingListOptions() {
      return this.mailingLists.data.map(mailingList => {
        return {
          value: mailingList.id,
          label: mailingList.attributes.name
        }
      })
    },

    timeBasedAdFormatOptions() {
      return this.timeBasedAdFormats.data.map(timeBasedAdFormat => {
        return {
          value: timeBasedAdFormat.id,
          label: timeBasedAdFormat.attributes.name
        }
      })
    },

    contentArticleFlagOptions() {
      return this.contentArticleFlags.data.map(contentArticleFlag => {
        return {
          value: contentArticleFlag.id,
          label: contentArticleFlag.attributes.name
        }
      })
    },
  },

  methods: {
    submit() {
      http.put('/api/v2/portals/' + this.portal.id, this.data)
          .then(response => {
            notify.success('The portal was saved')

            this.errors = {}

            this.$emit('portal-updated', response.data.data)
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
